import {create} from 'zustand';
import type {
    CompanyAnnual,
    BankConfirmation,
    AuditStaff,
    Director,
    Shareholder,
    SharesChangeRecord,
} from '@/services/accountService/companyAnnualService';
import {getAnnual} from '@/services/accountService/companyAnnualService';
import {
    resolveAuditSpanType,
    type AuditPeriodSlice,
    type AuditSpanType,
    type AuditSplitMode,
} from '@/utils/auditPeriodSplit';

/** In-flight getAnnual promises — concurrent loadAnnual(id) share one HTTP call */
const loadAnnualInflight = new Map<number, Promise<CompanyAnnual | null>>();

export type AuditPeriodSpan = {
    type: AuditSpanType;
    mode: AuditSplitMode;
    slices: AuditPeriodSlice[];
    periodFrom: string | null;
    periodTo: string | null;
    needsFirstAuditConfirm?: boolean;
};

interface YearOfAssessmentInfo {
    periodFrom: string | null;
    periodTo: string | null;
    lastYearPeriodFrom: string | null;
    lastYearPeriodTo: string | null;
    nextYearPeriodFrom: string | null;
    nextYearPeriodTo: string | null;
    yearOfAssessment: string | null;
    lastYearOfAssessment: string | null;
    nextYearOfAssessment: string | null;
}

interface CompanyAnnualState {
    // Cache key: annualId -> CompanyAnnual
    byAnnualId: Record<number, CompanyAnnual>;

    // Cache key: annualId -> SharesChangeRecord[]
    sharesChangeRecordsByAnnualId: Record<number, SharesChangeRecord[]>;

    // 当前激活的年度 id（FsAdjustment 挂载时设置，卸载时清空）
    currentAnnualId: number | null;

    // Load one annual and store it if API returns a value (force: bypass byAnnualId cache)
    loadAnnual: (annualId: number, opts?: {force?: boolean}) => Promise<CompanyAnnual | null>;

    // 初始化当前激活的 annual（设置 currentAnnualId 并确保 byAnnualId 已加载）
    initCurrentAnnual: (annualId: number) => Promise<void>;

    // 清除当前激活的 annualId（不清空 byAnnualId 缓存）
    clearCurrentAnnual: () => void;

    // 判断当前激活的年度是否为首次审计（firstAudit === 1）
    isCurrentFirstAudit: () => boolean;

    /**
     * SINGLE / MULTI period span for an annual (Phase 1).
     * Pass `inCorpDate` when available; otherwise firstAudit===1 uses periodFrom as proxy.
     */
    getAuditPeriodSpan: (
        annualId: number,
        opts?: {inCorpDate?: string | null},
    ) => Promise<AuditPeriodSpan>;

    // For Company Annual Detail
    getPeriodInfo: (annualId: number) => Promise<{
        periodFrom: string | null;
        periodTo: string | null;
        lastYearPeriodFrom: string | null;
        lastYearPeriodTo: string | null;
        yearOfAssessment: string | null;
        auditMonths: number | undefined;
        cessationDuringThisPeriod: number | undefined;
        isMultiYearAudit: boolean | undefined;
        firstAuditOver18Confirmed: number | undefined;
    }>;
    getCompanyDetailsInfo: (annualId: number) => Promise<{
        companyId: number | undefined;
        offShoreClaim: number | undefined;
        limitByGuarantee: number | undefined;
        limitByGuaranteeLocked: boolean | undefined;
        liabilitiesCurrency: string | undefined;
        liabilitiesAmount: number | undefined;
        otherNameThanCompany: string | null;
        section88: number | undefined;
        taxFileNo: string | null;
        changeOfAddress: number | undefined;
        changeDate: string | null;
        lastAddress: string | null;
    }>;
    getAuditOpinionInfo: (annualId: number) => Promise<{
        qualifyOpinion: number | undefined;
        firstAudit: number | undefined;
    }>;
    getOtherDisclosuresInfo: (annualId: number) => Promise<{
        ppe: number | undefined;
        relatedPartyTransactions: number | undefined;
        rentalCommitment: number | undefined;
    }>;
    getHoldingStructureInfo: (annualId: number) => Promise<{
        holdingCo: number;
        nameOfImmediate: string | null;
        immediateHolding: string | null;
        nameOfUltimate: string | null;
        ultimateHolding: string | null;
    }>;
    getAllotmentOfSharesInfo: (annualId: number) => Promise<{
        allotmentOfShares: number | undefined;
        noOfShares: number | undefined;
        pricePerShare: number | undefined;
        allotmentAmount: number | undefined;
        priceCurrency: string | null;
    }>;
    getShareholdersInfo: (annualId: number) => Promise<Shareholder[]>;
    getDirectorsInfo: (annualId: number) => Promise<Director[]>;
    // Dividend (派息)
    getDividendInfo: (annualId: number) => Promise<{
        dividendPaid: number | undefined;
        dividendCurrency: string | null;
        amountPerEachShare: number | undefined;
        totalDividendAmount: number | undefined;
        dividendPayableBf: number | undefined;
        dividendDeclared: number | undefined;
        dividendPaidAmount: number | undefined;
    }>;
    // Company Name & Auditor Changes (公司名稱與審計師變更)
    getCompanyAndAuditorChangesInfo: (annualId: number) => Promise<{
        isChangeName: number | undefined;
        changeEffectiveDate: string | null;
        companyName: string | null;
        companyChineseName: string | null;
        currentCompanyName: string | null;
        currentCompanyChineseName: string | null;
        oldCompanyName: string | null;
        oldCompanyChineseName: string | null;
        changeOfFinancialYearEnd: number | undefined;
        reasonToChange: string | null;
        dateOfBoardResolution: string | null;
        formerAuditor: string;
        addressOfFormerAuditor: string;
        professionalClearance: string | null;
    }>;
    // Business (業務)
    getBusinessInfo: (annualId: number) => Promise<{
        principalActivities: number | undefined;
        businessNature: string | null;
        commenceWithinPeriod: number | undefined;
        commenceDate: string | null;
    }>
    // Currency & Exchange Rate (貨幣與匯率)
    getCurrencyAndExchangeRateInfo: (annualId: number) => Promise<{
        presentationCurrency: string | null;
        exchangeRate: number | undefined;
        functionalCurrency: string | null;
    }>;
    // Small Cash Confirmation (小額現金詢證)
    getSmallCashConfirmationInfo: (annualId: number) => Promise<{
        pettyCashConfirmation: string;
        pettyCashAmount: number | undefined;
    }>;
    // Tax (稅務)
    getTaxInfo: (annualId: number) => Promise<{
        twoTierTaxThisYear: number | undefined;
        twoTierTaxLastYear: number | undefined;
        assessableResultType: number | undefined;
        assessableAmount: number | undefined;
        taxLossBF: number | undefined;
        taxLossCF: number | undefined;
    }>;
    // Audit Staffs (審計人員)
    getAuditStaffsInfo: (annualId: number) => Promise<AuditStaff[]>;
    // Auditor (審計師)
    getAuditorInfo: (annualId: number) => Promise<{
        auditCertificateId: number | undefined;
        irdLetterSigner: string;
    }>;
    // Tax Representative (稅務代表) — optional override for PTR Part 4.2 / Part 6
    getTaxRepInfo: (annualId: number) => Promise<{
        taxRepSameAsAuditor: number;
        taxRepName: string;
        taxRepAddress: string;
        taxRepTel: string;
        taxRepBrNumber: string;
        taxRepCertificateId: number | null;
        taxRepId: number | null;
        taxRepHerebyAuthorize: string;
    }>;
    // Other (其他)
    getOtherInfo: (annualId: number) => Promise<{
        referrer: string | null;
        inCharge: string | null;
    }>;

    // Getters (will auto-load if not cached yet)
    getYearOfAssessment: (annualId: number) => Promise<YearOfAssessmentInfo>;
    getBankConfirmations: (annualId: number) => Promise<BankConfirmation[]>;

    // Currency
    getPresentationCurrency: (annualId: number) => Promise<string | null>;

    // New getters
    getRegisteredOffice: (annualId: number) => Promise<{
        registeredOffice: string | null;
        currentRegisteredOffice: string | null;
        changeOfAddress: number;
        lastAddress: string | null;
        changeDate: string | null;
    }>;

    // Nature & Scope
    getNatureAndScope: (annualId: number) => Promise<{
        offShoreClaim: number;
        principalActivities: number;
        businessNature: string | null;
        commenceWithinPeriod: number;
        commenceDate: string | null;
    }>;

    // Tax Computation Info
    getTaxComputationInfo: (annualId: number) => Promise<{
        companyName: string;
        yearOfAssessment: string | null;
        presentationCurrency: string | null;
        taxFileNo: string | null;
    }>;

    // Optional helpers
    setAnnual: (annual: CompanyAnnual) => void;
    clear: () => void;

    // Setters
    setBankConfirmations: (annualId: number, bankConfirmations: BankConfirmation[]) => void;
    setAuditStaffs: (annualId: number, auditStaffs: AuditStaff[]) => void;
    setRegisteredOffice: (
        annualId: number,
        v: {
            registeredOffice: string | null;
            currentRegisteredOffice?: string | null;
            changeOfAddress: number;
            lastAddress: string | null;
            changeDate: string | null;
        }
    ) => void;
    setDirectors: (annualId: number, directors: Director[]) => void;
    setShareholders: (annualId: number, shareholders: Shareholder[]) => void;
    setHasHoldingCo: (
        annualId: number,
        v: {
            holdingCo: number;
            nameOfImmediate: string | null;
            immediateHolding: string | null;
            nameOfUltimate: string | null;
            ultimateHolding: string | null;
        }
    ) => void;
    setNatureAndScope: (
        annualId: number,
        v: {
            offShoreClaim: number;
            principalActivities: number;
            businessNature: string | null;
            commenceWithinPeriod?: number;
            commenceDate?: string | null;
        }
    ) => void;

    // Shares Change Records
    getSharesChangeRecords: (annualId: number, shareholderName?: string | null) => Promise<SharesChangeRecord[]>;
    setShareChangeRecords: (
        mode: 'transferShares' | 'allotmentShares',
        annualId: number,
        payload:
            | {
            selected: {
                id: number | null;
                shareholderName: string;
                numberOfShares: number; // after transfer
                valueOfShare: number;
                currency: string;
                changeDate: string | null;
            };
            transfers: Array<{
                id: number | null;
                shareholderName: string;
                transferOfShares: number;
                valueOfShare: number;
                changeDate: string | null;
                currency: string;
            }>;
        }
            | Array<{
            id: number | null;
            shareholderName: string;
            allotmentOfShares: number;
            valueOfEachShare: number | null;
            exchangeRate: number | null;
            currency: string | null;
            changeDate: string | null;
        }>
    ) => void;
}

export const useCompanyAnnualStore = create<CompanyAnnualState>((set, get) => ({
    byAnnualId: {},
    sharesChangeRecordsByAnnualId: {},
    currentAnnualId: null,

    setAnnual: (annual) => set((s) => ({byAnnualId: {...s.byAnnualId, [Number(annual.annualId)]: annual}})),

    clear: () => set({byAnnualId: {}, currentAnnualId: null}),

    initCurrentAnnual: async (annualId: number) => {
        const id = Number(annualId);
        if (!id || id <= 0 || Number.isNaN(id)) return;
        set({currentAnnualId: id});
        const s = get();
        if (!s.byAnnualId[id]) {
            await s.loadAnnual(id);
        }
    },

    clearCurrentAnnual: () => set({currentAnnualId: null}),

    isCurrentFirstAudit: () => {
        const s = get();
        const id = s.currentAnnualId;
        if (!id) return false;
        return Number(s.byAnnualId[id]?.firstAudit ?? 0) === 1;
    },

    getAuditPeriodSpan: async (annualId, opts) => {
        const s = get();
        let annual = s.byAnnualId[Number(annualId)];
        if (!annual) annual = (await s.loadAnnual(annualId)) || ({} as CompanyAnnual);
        const periodFrom = annual?.periodFrom ?? null;
        const periodTo = annual?.periodTo ?? null;
        // firstAudit=1 → YE split using engagement start as proxy, even when company inCorp ≠ periodFrom
        // (avoids REGULAR anniversary colliding two slices onto the same YOA).
        const inCorpDate = Number(annual?.firstAudit ?? 0) === 1
            ? periodFrom
            : (opts?.inCorpDate ?? null);
        const resolved = resolveAuditSpanType({
            periodFrom,
            periodTo,
            inCorpDate,
            cessationDuringThisPeriod: annual?.cessationDuringThisPeriod ?? null,
            firstAuditFollows18Months: annual?.firstAuditFollows18Months ?? null,
            firstAuditYearEndMD: annual?.firstAuditYearEndMD ?? null,
            isMultiYearAudit: annual?.isMultiYearAudit ?? null,
            lastYearPeriodTo: annual?.lastYearPeriodTo ?? null,
        });
        return {
            type: resolved.type,
            mode: resolved.mode,
            slices: resolved.slices,
            periodFrom,
            periodTo,
            needsFirstAuditConfirm: resolved.needsFirstAuditConfirm,
        };
    },

    // Setters
    setBankConfirmations: (annualId, bankConfirmations) =>
        set((s) => {
            const id = Number(annualId);
            const existing = s.byAnnualId[id] || ({annualId: id} as CompanyAnnual);
            const updated: CompanyAnnual = {...existing, bankConfirmations};
            return {byAnnualId: {...s.byAnnualId, [id]: updated}};
        }),

    setAuditStaffs: (annualId, auditStaffs) =>
        set((s) => {
            const id = Number(annualId);
            const existing = s.byAnnualId[id] || ({annualId: id} as CompanyAnnual);
            const updated: CompanyAnnual = {...existing, auditStaffs};
            return {byAnnualId: {...s.byAnnualId, [id]: updated}};
        }),

    setRegisteredOffice: (annualId, v) =>
        set((s) => {
            const id = Number(annualId);
            const existing = s.byAnnualId[id] || ({annualId: id} as CompanyAnnual);
            const updated: CompanyAnnual = {
                ...existing,
                registeredOffice: v.registeredOffice,
                currentRegisteredOffice: v.currentRegisteredOffice ?? v.registeredOffice,
                changeOfAddress: v.changeOfAddress,
                lastAddress: v.lastAddress,
                changeDate: v.changeDate,
            };
            return {byAnnualId: {...s.byAnnualId, [id]: updated}};
        }),

    setDirectors: (annualId, directors) =>
        set((s) => {
            const id = Number(annualId);
            const existing = s.byAnnualId[id] || ({annualId: id} as CompanyAnnual);
            const updated: CompanyAnnual = {...existing, directors};
            return {byAnnualId: {...s.byAnnualId, [id]: updated}};
        }),

    setShareholders: (annualId, shareholders) =>
        set((s) => {
            const id = Number(annualId);
            const existing = s.byAnnualId[id] || ({annualId: id} as CompanyAnnual);
            const updated: CompanyAnnual = {...existing, shareholders};
            return {byAnnualId: {...s.byAnnualId, [id]: updated}};
        }),

    setHasHoldingCo: (annualId, v) =>
        set((s) => {
            const id = Number(annualId);
            const existing = s.byAnnualId[id] || ({annualId: id} as CompanyAnnual);
            const updated: CompanyAnnual = {
                ...existing,
                holdingCo: v.holdingCo,
                nameOfImmediate: v.nameOfImmediate,
                immediateHolding: v.immediateHolding,
                nameOfUltimate: v.nameOfUltimate,
                ultimateHolding: v.ultimateHolding,
            };
            return {byAnnualId: {...s.byAnnualId, [id]: updated}};
        }),

    setNatureAndScope: (annualId, v) =>
        set((s) => {
            const id = Number(annualId);
            const existing = s.byAnnualId[id] || ({annualId: id} as CompanyAnnual);
            const updated: CompanyAnnual = {
                ...existing,
                offShoreClaim: Number(v.offShoreClaim ?? 0),
                principalActivities: Number(v.principalActivities ?? 0),
                businessNature: v.businessNature ?? null,
                commenceWithinPeriod: Number(v.commenceWithinPeriod ?? 1),
                commenceDate: v.commenceWithinPeriod === 0 ? null : (v.commenceDate ?? null),
            };
            return {byAnnualId: {...s.byAnnualId, [id]: updated}};
        }),

    // Shares Change Records
    getSharesChangeRecords: async (annualId: number, shareholderName?: string | null) => {
        const s = get();
        const id = Number(annualId);
        const all = Array.isArray(s.sharesChangeRecordsByAnnualId[id])
            ? s.sharesChangeRecordsByAnnualId[id]
            : [];
        if (!shareholderName) return all;
        const name = String(shareholderName);
        return all.filter((r) => (r.shareholderName || '') === name);
    },
    setShareChangeRecords: (mode, annualId, payload) =>
        set((s) => {
            const id = Number(annualId);
            const existingRecords = Array.isArray(s.sharesChangeRecordsByAnnualId[id])
                ? s.sharesChangeRecordsByAnnualId[id]
                : [];

            let nextRecords: SharesChangeRecord[] = existingRecords;
            let nextShareholders: Shareholder[] | undefined = undefined;

            const currentAnnual = s.byAnnualId[id] || ({annualId: id} as CompanyAnnual);
            const currentShareholders = Array.isArray(currentAnnual.shareholders)
                ? [...(currentAnnual.shareholders as Shareholder[])]
                : [];

            if (mode === 'transferShares') {
                const data = payload as any as {
                    selected: {
                        id: number | null;
                        shareholderName: string;
                        numberOfShares: number;
                        valueOfShare: number;
                        currency: string;
                        changeDate: string | null;
                    };
                    transfers: Array<{
                        id: number | null;
                        shareholderName: string;
                        transferOfShares: number;
                        valueOfShare: number;
                        changeDate: string | null;
                        currency: string;
                    }>;
                };

                // Build records for transfer: positive for receivers, negative for source
                const built: SharesChangeRecord[] = [];
                for (const t of data.transfers || []) {
                    built.push({
                        id: 0 as any,
                        annualId: id,
                        shareholderName: t.shareholderName,
                        currency: t.currency ?? data.selected.currency,
                        valueOfEachShare: (t as any).valueOfShare ?? data.selected.valueOfShare,
                        exchangeRate: null,
                        transferOfShares: Math.abs(Number(t.transferOfShares || 0)),
                        allotmentOfShares: null,
                        changeDate: t.changeDate ?? data.selected.changeDate ?? null,
                    });
                    built.push({
                        id: 0 as any,
                        annualId: id,
                        shareholderName: data.selected.shareholderName,
                        currency: t.currency ?? data.selected.currency,
                        valueOfEachShare: (t as any).valueOfShare ?? data.selected.valueOfShare,
                        exchangeRate: null,
                        transferOfShares: -Math.abs(Number(t.transferOfShares || 0)),
                        allotmentOfShares: null,
                        changeDate: t.changeDate ?? data.selected.changeDate ?? null,
                    });
                }
                nextRecords = [...existingRecords, ...built];

                // Update shareholders share counts
                const next = [...currentShareholders];
                // Update source
                const srcIdx = next.findIndex((r) => (r.id ?? null) === (data.selected.id ?? null) || (r.shareholderName || '') === data.selected.shareholderName);
                if (srcIdx >= 0) {
                    next[srcIdx] = {
                        ...next[srcIdx],
                        shareholderName: data.selected.shareholderName,
                        noOfShares: Number(data.selected.numberOfShares) || 0,
                        valueOfEachShare: Number(data.selected.valueOfShare) || Number(next[srcIdx].valueOfEachShare) || 0,
                    } as Shareholder;
                }
                // Apply inbound transfers
                for (const t of data.transfers || []) {
                    let idx = -1;
                    if (t.id !== null && t.id !== undefined) {
                        idx = next.findIndex((r) => (r.id ?? null) === t.id);
                    }
                    if (idx === -1) idx = next.findIndex((r) => (r.shareholderName || '') === t.shareholderName);
                    if (idx >= 0) {
                        next[idx] = {
                            ...next[idx],
                            shareholderName: t.shareholderName,
                            noOfShares: (Number(next[idx].noOfShares) || 0) + Number(t.transferOfShares || 0),
                            valueOfEachShare: Number((t as any).valueOfShare) || Number(next[idx].valueOfEachShare) || 0,
                        } as Shareholder;
                    } else {
                        next.push({
                            id: (t.id as any) ?? null,
                            shareholderName: t.shareholderName,
                            gender: 0,
                            acquireDuringPeriod: null,
                            currency: (data.selected as any).currency || 'HKD',
                            noOfShares: Number(t.transferOfShares) || 0,
                            exchangeRate: 0,
                            transferOfShares: 0,
                            allotmentOfShares: 0,
                            valueOfEachShare: Number((t as any).valueOfShare) || 0,
                        } as Shareholder);
                    }
                }
                nextShareholders = next;
            } else if (mode === 'allotmentShares') {
                const items = payload as any as Array<{
                    id: number | null;
                    shareholderName: string;
                    allotmentOfShares: number;
                    valueOfEachShare: number | null;
                    exchangeRate: number | null;
                    currency: string | null;
                    changeDate: string | null;
                }>;

                const built: SharesChangeRecord[] = (items || []).map((it) => ({
                    id: 0 as any,
                    annualId: id,
                    shareholderName: it.shareholderName,
                    currency: it.currency ?? null,
                    valueOfEachShare: it.valueOfEachShare ?? null,
                    exchangeRate: it.exchangeRate ?? null,
                    transferOfShares: null,
                    allotmentOfShares: Number(it.allotmentOfShares || 0),
                    changeDate: it.changeDate ?? null,
                }));
                nextRecords = [...existingRecords, ...built];

                const next = [...currentShareholders];
                const byKey = new Map<string, { sum: number; it: any }>();
                for (const it of items || []) {
                    const key = it.id != null ? `id:${it.id}` : `name:${it.shareholderName}`;
                    const cur = byKey.get(key);
                    if (cur) cur.sum += Number(it.allotmentOfShares || 0);
                    else byKey.set(key, {sum: Number(it.allotmentOfShares || 0), it});
                }
                for (const [key, {sum, it}] of byKey.entries()) {
                    let idx = -1;
                    if (key.startsWith('id:') && it.id != null) idx = next.findIndex((r) => (r.id ?? null) === it.id);
                    if (idx === -1) idx = next.findIndex((r) => (r.shareholderName || '') === it.shareholderName);
                    if (idx >= 0) {
                        next[idx] = {
                            ...next[idx],
                            shareholderName: it.shareholderName,
                            noOfShares: (Number(next[idx].noOfShares) || 0) + sum,
                            valueOfEachShare: Number(it.valueOfEachShare ?? next[idx].valueOfEachShare ?? 0),
                            exchangeRate: Number(it.exchangeRate ?? next[idx].exchangeRate ?? 0),
                            currency: (it.currency as any) || (next[idx] as any).currency || 'HKD',
                        } as Shareholder;
                    } else {
                        next.push({
                            id: it.id ?? null,
                            shareholderName: it.shareholderName,
                            gender: 0,
                            acquireDuringPeriod: null,
                            currency: (it.currency as any) || 'HKD',
                            noOfShares: Number(sum) || 0,
                            exchangeRate: Number(it.exchangeRate) || 0,
                            transferOfShares: 0,
                            allotmentOfShares: 0,
                            valueOfEachShare: Number(it.valueOfEachShare) || 0,
                        } as Shareholder);
                    }
                }
                nextShareholders = next;
            }

            const nextAnnual: CompanyAnnual = {
                ...currentAnnual,
                shareholders: nextShareholders ?? currentShareholders,
            } as CompanyAnnual;

            return {
                byAnnualId: {...s.byAnnualId, [id]: nextAnnual},
                sharesChangeRecordsByAnnualId: {...s.sharesChangeRecordsByAnnualId, [id]: nextRecords},
            };
        }),

    loadAnnual: async (annualId: number, opts?: {force?: boolean}) => {
        if (!annualId || annualId <= 0 || Number.isNaN(annualId)) return null;
        const id = Number(annualId);

        if (!opts?.force) {
            const cached = get().byAnnualId[id];
            if (cached) return cached;
        }

        const inflight = loadAnnualInflight.get(id);
        if (inflight) return inflight;

        const promise = (async (): Promise<CompanyAnnual | null> => {
            try {
                const data = await getAnnual(id);
                if (data && typeof (data as any).annualId !== 'undefined') {
                    set((s) => ({byAnnualId: {...s.byAnnualId, [id]: data}}));
                    return data;
                }
            } catch (e) {
                // WHY: callers often treat null as empty; keep that contract but log the real failure
                console.error('[DEBUG-annual-load] loadAnnual failed', id, e);
            } finally {
                loadAnnualInflight.delete(id);
            }
            return null;
        })();

        loadAnnualInflight.set(id, promise);
        return promise;
    },

    getPeriodInfo: async (annualId: number) => {
        const s = get();
        let annual = s.byAnnualId[Number(annualId)];
        if (!annual) annual = (await s.loadAnnual(annualId)) || ({} as CompanyAnnual);
        return {
            periodFrom: annual?.periodFrom ?? null,
            periodTo: annual?.periodTo ?? null,
            lastYearPeriodFrom: annual?.lastYearPeriodFrom ?? null,
            lastYearPeriodTo: annual?.lastYearPeriodTo ?? null,
            yearOfAssessment: annual?.yearOfAssessment ?? null,
            auditMonths: annual?.auditMonths ?? undefined,
            cessationDuringThisPeriod: annual?.cessationDuringThisPeriod ?? undefined,
            isMultiYearAudit: annual?.isMultiYearAudit ?? true,
            firstAuditOver18Confirmed: annual?.firstAuditOver18Confirmed ?? 0,
        }
    },
    getCompanyDetailsInfo: async (annualId: number) => {
        const s = get();
        let annual = s.byAnnualId[Number(annualId)];
        if (!annual) annual = (await s.loadAnnual(annualId)) || ({} as CompanyAnnual);
        return {
            companyId: annual?.companyId ?? undefined,
            offShoreClaim: annual?.offShoreClaim ?? undefined,
            limitByGuarantee: Number(annual?.limitByGuarantee ?? 0),
            limitByGuaranteeLocked: Boolean(annual?.limitByGuaranteeLocked),
            liabilitiesCurrency: annual?.liabilitiesCurrency,
            liabilitiesAmount: Number(annual?.liabilitiesAmount ?? 0),
            otherNameThanCompany: annual?.otherNameThanCompany ?? null,
            section88: Number(annual?.section88 ?? 0),
            taxFileNo: annual?.taxFileNo ?? null,
            changeOfAddress: annual?.changeOfAddress ?? undefined,
            changeDate: annual?.changeDate ?? null,
            lastAddress: annual?.lastAddress ?? null,
        }
    },
    getAuditOpinionInfo: async (annualId: number) => {
        const s = get();
        let annual = s.byAnnualId[Number(annualId)];
        if (!annual) annual = (await s.loadAnnual(annualId)) || ({} as CompanyAnnual);
        return {
            qualifyOpinion: annual?.qualifyOpinion === null || annual?.qualifyOpinion === undefined
                ? undefined
                : Number(annual.qualifyOpinion),
            firstAudit: annual?.firstAudit === null || annual?.firstAudit === undefined
                ? undefined
                : Number(annual.firstAudit),
        }
    },
    getOtherDisclosuresInfo: async (annualId: number) => {
        const s = get();
        let annual = s.byAnnualId[Number(annualId)];
        if (!annual) annual = (await s.loadAnnual(annualId)) || ({} as CompanyAnnual);
        return {
            ppe: Number(annual?.ppe ?? 0),
            relatedPartyTransactions: annual?.relatedPartyTransactions ?? 0,
            rentalCommitment: annual?.rentalCommitment ?? 0,
        }
    },
    getHoldingStructureInfo: async (annualId: number) => {
        const s = get();
        let annual = s.byAnnualId[Number(annualId)];
        if (!annual) annual = (await s.loadAnnual(annualId)) || ({} as CompanyAnnual);
        return {
            holdingCo: Number(annual?.holdingCo ?? 0),
            nameOfImmediate: annual?.nameOfImmediate ?? null,
            immediateHolding: annual?.immediateHolding ?? null,
            nameOfUltimate: annual?.nameOfUltimate ?? null,
            ultimateHolding: annual?.ultimateHolding ?? null,
        };
    },
    getAllotmentOfSharesInfo: async (annualId: number) => {
        const s = get();
        let annual = s.byAnnualId[Number(annualId)];
        if (!annual) annual = (await s.loadAnnual(annualId)) || ({} as CompanyAnnual);
        return {
            allotmentOfShares: Number(annual?.allotmentOfShares ?? 0),
            noOfShares: annual?.noOfShares ?? 0,
            pricePerShare: annual?.pricePerShare ?? 0,
            allotmentAmount: annual?.allotmentAmount ?? 0,
            priceCurrency: annual?.priceCurrency ?? '',
        };
    },
    getDividendInfo: async (annualId: number) => {
        const s = get();
        let annual = s.byAnnualId[Number(annualId)];
        if (!annual) annual = (await s.loadAnnual(annualId)) || ({} as CompanyAnnual);
        return {
            dividendPaid: annual?.dividendPaid ?? undefined,
            dividendCurrency: annual?.dividendCurrency ?? null,
            amountPerEachShare: annual?.amountPerEachShare ?? undefined,
            totalDividendAmount: annual?.totalDividendAmount ?? undefined,
            dividendPayableBf: annual?.dividendPayableBf ?? undefined,
            dividendDeclared: annual?.dividendDeclared ?? undefined,
            dividendPaidAmount: annual?.dividendPaidAmount ?? undefined,
        };
    },
    getCompanyAndAuditorChangesInfo: async (annualId: number) => {
        const s = get();
        let annual = s.byAnnualId[Number(annualId)];
        if (!annual) annual = (await s.loadAnnual(annualId)) || ({} as CompanyAnnual);
        return {
            isChangeName: annual?.isChangeName ?? undefined,
            changeEffectiveDate: annual?.changeEffectiveDate ?? null,
            companyName: annual?.companyName ?? null,
            companyChineseName: annual?.companyChineseName ?? null,
            currentCompanyName: annual?.currentCompanyName ?? null,
            currentCompanyChineseName: annual?.currentCompanyChineseName ?? null,
            oldCompanyName: annual?.oldCompanyName ?? null,
            oldCompanyChineseName: annual?.oldCompanyChineseName ?? null,
            changeOfFinancialYearEnd: annual?.changeOfFinancialYearEnd ?? undefined,
            reasonToChange: annual?.reasonToChange ?? null,
            dateOfBoardResolution: annual?.dateOfBoardResolution ?? null,
            formerAuditor: annual?.formerAuditor ?? '',
            addressOfFormerAuditor: annual?.addressOfFormerAuditor ?? '',
            professionalClearance: annual?.professionalClearance ?? null,
        };
    },

    getBusinessInfo: async (annualId: number) => {
        const s = get();
        let annual = s.byAnnualId[Number(annualId)];
        if (!annual) annual = (await s.loadAnnual(annualId)) || ({} as CompanyAnnual);
        return {
            principalActivities: annual?.principalActivities ?? undefined,
            businessNature: annual?.businessNature ?? null,
            commenceWithinPeriod: annual?.commenceWithinPeriod ?? undefined,
            commenceDate: annual?.commenceDate ?? null,
        };
    },

    getCurrencyAndExchangeRateInfo: async (annualId: number) => {
        const s = get();
        let annual = s.byAnnualId[Number(annualId)];
        if (!annual) annual = (await s.loadAnnual(annualId)) || ({} as CompanyAnnual);
        return {
            presentationCurrency: annual?.presentationCurrency ?? null,
            exchangeRate: annual?.exchangeRate ?? undefined,
            functionalCurrency: annual?.functionalCurrency ?? null,
        };
    },
    getSmallCashConfirmationInfo: async (annualId: number) => {
        const s = get();
        let annual = s.byAnnualId[Number(annualId)];
        if (!annual) annual = (await s.loadAnnual(annualId)) || ({} as CompanyAnnual);
        return {
            pettyCashConfirmation: annual?.pettyCashConfirmation ?? '',
            pettyCashAmount: annual?.pettyCashAmount ?? undefined,
        };
    },
    getTaxInfo: async (annualId: number) => {
        const s = get();
        let annual = s.byAnnualId[Number(annualId)];
        if (!annual) annual = (await s.loadAnnual(annualId)) || ({} as CompanyAnnual);
        return {
            twoTierTaxThisYear: annual?.twoTierTaxThisYear ?? undefined,
            twoTierTaxLastYear: annual?.twoTierTaxLastYear ?? undefined,
            assessableResultType: annual?.assessableResultType ?? undefined,
            assessableAmount: annual?.assessableAmount ?? undefined,
            taxLossBF: annual?.taxLossBF ?? undefined,
            taxLossCF: annual?.taxLossCF ?? undefined,
        };
    },
    getAuditStaffsInfo: async (annualId: number) => {
        const s = get();
        let annual = s.byAnnualId[Number(annualId)];
        if (!annual) annual = (await s.loadAnnual(annualId)) || ({} as CompanyAnnual);
        return Array.isArray(annual?.auditStaffs) ? annual!.auditStaffs : [];
    },
    getAuditorInfo: async (annualId: number) => {
        const s = get();
        let annual = s.byAnnualId[Number(annualId)];
        if (!annual) annual = (await s.loadAnnual(annualId)) || ({} as CompanyAnnual);
        return {
            auditCertificateId: annual?.auditCertificateId ?? undefined,
            irdLetterSigner: annual?.irdLetterSigner ?? '',
        };
    },
    getTaxRepInfo: async (annualId: number) => {
        const s = get();
        let annual = s.byAnnualId[Number(annualId)];
        if (!annual) annual = (await s.loadAnnual(annualId)) || ({} as CompanyAnnual);
        return {
            taxRepSameAsAuditor: annual?.taxRepSameAsAuditor ?? 1,
            taxRepName: annual?.taxRepName ?? '',
            taxRepAddress: annual?.taxRepAddress ?? '',
            taxRepTel: annual?.taxRepTel ?? '',
            taxRepBrNumber: annual?.taxRepBrNumber ?? '',
            taxRepCertificateId: annual?.taxRepCertificateId ?? null,
            taxRepId: annual?.taxRepId ?? null,
            taxRepHerebyAuthorize: annual?.taxRepHerebyAuthorize ?? '',
        };
    },
    getOtherInfo: async (annualId: number) => {
        const s = get();
        let annual = s.byAnnualId[Number(annualId)];
        if (!annual) annual = (await s.loadAnnual(annualId)) || ({} as CompanyAnnual);
        return {
            referrer: annual?.referrer ?? null,
            inCharge: annual?.inCharge ?? null,
        };
    },
    getShareholdersInfo: async (annualId: number) => {
        const s = get();
        let annual = s.byAnnualId[Number(annualId)];
        if (!annual) annual = (await s.loadAnnual(annualId)) || ({} as CompanyAnnual);
        return Array.isArray(annual?.shareholders) ? annual!.shareholders : [];
    },
    getDirectorsInfo: async (annualId: number) => {
        const s = get();
        let annual = s.byAnnualId[Number(annualId)];
        if (!annual) annual = (await s.loadAnnual(annualId)) || ({} as CompanyAnnual);
        return Array.isArray(annual?.directors) ? annual!.directors : [];
    },

    getYearOfAssessment: async (annualId: number) => {
        const s = get();
        let annual = s.byAnnualId[Number(annualId)];
        if (!annual) annual = (await s.loadAnnual(annualId)) || ({} as CompanyAnnual);
        // Compute next year period based on periodTo and the rule:
        // If periodTo is before or on 31/3/yyyy, next range is 1/4/yyyy to 31/3/(yyyy+1)
        // If periodTo is after 31/3/yyyy, next range is 1/4/(yyyy+1) to 31/3/(yyyy+2)
        const parseDate = (v?: string | null): Date | null => {
            if (!v) return null;
            // support formats: YYYY-MM-DD or DD/MM/YYYY
            if (v.includes('-')) {
                const d = new Date(v);
                return isNaN(d.getTime()) ? null : d;
            }
            if (v.includes('/')) {
                const [dd, mm, yyyy] = v.split('/').map((x) => x.trim());
                const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
                return isNaN(d.getTime()) ? null : d;
            }
            const d = new Date(v);
            return isNaN(d.getTime()) ? null : d;
        };

        const formatDMY = (d: Date | null): string | null => {
            if (!d) return null;
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            return `${dd}/${mm}/${yyyy}`;
        };

        // Compute Year of Assessment in format yyyy/yy+1 (e.g., 2024/25)
        // Rule: For a period that ends between 1 Apr yyyy and 31 Mar (yyyy+1), YOA is yyyy/(yy+1)
        const calcYOA = (toDate: Date | null): string | null => {
            if (!toDate) return null;
            // If month is Jan/Feb/Mar => falls into previous yyyy
            const month = toDate.getMonth() + 1; // 1..12
            const yyyy = month >= 4 ? toDate.getFullYear() : toDate.getFullYear() - 1;
            const nextYY = String((yyyy + 1) % 100).padStart(2, '0');
            return `${yyyy}/${nextYY}`;
        };

        const pt = parseDate(annual?.periodTo ?? null);
        const lastPt = parseDate(annual?.lastYearPeriodTo ?? null);
        let nextFrom: Date | null = null;
        let nextTo: Date | null = null;
        if (pt) {
            // nextFrom = periodTo + 1 day
            nextFrom = new Date(pt.getFullYear(), pt.getMonth(), pt.getDate() + 1);
            // nextTo = periodTo + 1 year
            nextTo = new Date(pt.getFullYear() + 1, pt.getMonth(), pt.getDate());
        }
        const yoa = calcYOA(pt);
        const lastYoa = calcYOA(lastPt);
        const nextYoa = calcYOA(nextTo);
        return {
            periodFrom: annual?.periodFrom ?? null,
            periodTo: annual?.periodTo ?? null,
            lastYearPeriodFrom: annual?.lastYearPeriodFrom ?? null,
            lastYearPeriodTo: annual?.lastYearPeriodTo ?? null,
            nextYearPeriodFrom: formatDMY(nextFrom),
            nextYearPeriodTo: formatDMY(nextTo),
            yearOfAssessment: yoa,
            lastYearOfAssessment: lastYoa,
            nextYearOfAssessment: nextYoa,
        } as YearOfAssessmentInfo;
    },

    getPresentationCurrency: async (annualId: number) => {
        const s = get();
        let annual = s.byAnnualId[Number(annualId)];
        if (!annual) annual = (await s.loadAnnual(annualId)) || ({} as CompanyAnnual);
        const cur = annual?.presentationCurrency ?? null;
        return cur || null;
    },

    getBankConfirmations: async (annualId: number) => {
        const s = get();
        let annual = s.byAnnualId[Number(annualId)];
        if (!annual) annual = (await s.loadAnnual(annualId)) || ({} as CompanyAnnual);
        return Array.isArray(annual?.bankConfirmations) ? annual!.bankConfirmations : [];
    },

    getRegisteredOffice: async (annualId: number) => {
        const s = get();
        let annual = s.byAnnualId[Number(annualId)];
        if (!annual) annual = (await s.loadAnnual(annualId)) || ({} as CompanyAnnual);
        const current = annual?.currentRegisteredOffice ?? annual?.registeredOffice ?? null;
        return {
            registeredOffice: annual?.registeredOffice ?? null,
            currentRegisteredOffice: current,
            changeOfAddress: Number(annual?.changeOfAddress ?? 0),
            lastAddress: annual?.lastAddress ?? null,
            changeDate: annual?.changeDate ?? null,
        };
    },

    getNatureAndScope: async (annualId: number) => {
        const s = get();
        let annual = s.byAnnualId[Number(annualId)];
        if (!annual) annual = (await s.loadAnnual(annualId)) || ({} as CompanyAnnual);
        return {
            offShoreClaim: Number(annual?.offShoreClaim ?? 0),
            principalActivities: Number(annual?.principalActivities ?? 0),
            businessNature: annual?.businessNature ?? null,
            commenceWithinPeriod: Number(annual?.commenceWithinPeriod ?? 1),
            commenceDate: annual?.commenceDate ?? null,
        };
    },

    getTaxComputationInfo: async (annualId: number) => {
        const s = get();
        let annual = s.byAnnualId[Number(annualId)];
        if (!annual) annual = (await s.loadAnnual(annualId)) || ({} as CompanyAnnual);
        return {
            companyName: annual?.companyName ?? '',
            yearOfAssessment: annual?.yearOfAssessment ?? null,
            presentationCurrency: annual?.presentationCurrency ?? null,
            taxFileNo: annual?.taxFileNo ?? null,
        };
    },
}));
